'use client';

import {
  useCallback, useContext, useEffect, useMemo, useRef, useState, FocusEvent, KeyboardEvent, MouseEvent, ReactNode,
} from 'react';
import { useTranslation } from 'next-i18next';
import {
  AdjustmentsHorizontalIcon, ChevronDownIcon, MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { MicrophoneIcon, StopIcon } from '@heroicons/react/24/solid';
import { Button, bjToast } from '@components/common';
import { PopoverStyled, PopoverStyledButton, PopoverStyledPanel } from '@components/common/Popover/PopoverStyled';
import { FilterDrawerContext } from '@components/contexts/FilterDrawerContext';
import { HeaderSearchContext } from '@components/contexts/HeaderSearchContext';
import { useCandidateNavigationOptional, type CandidateSearchFilters } from '@components/contexts/CandidateNavigationContext';
import { countAppliedFilters } from '@components/features/filters/helpers/countAppliedFilters';
import { MapButton } from '@components/layout/Header/MainNavbar/search/MapButton';
import { useSwrNextJobs } from '@hooks/useSwrApi/endpoints/next-jobs';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { useAutoResizeTextarea } from '@hooks/useAutoResizeTextarea';
import { useVoiceToText } from '@hooks/voice/useVoiceToText';
import { slugify } from '@utils/strings/slugify';
import { isSearchSourcedRow } from './nextJobSearchMatch';
import { VoiceWaveform } from './VoiceWaveform';
import { VoiceHintBubble } from './VoiceHintBubble';

interface SearchBranchProps {
  locale: Locale;
  onSubmit: (filters: CandidateSearchFilters) => void;
  leftSlot?: ReactNode;
}

// Compare slugified locations — URL-derived values arrive lowercased + de-diacriticised
// (`bucuresti`) while saved alerts keep the canonical form (`București`).
const sameLocations = (a?: string[] | null, b?: string[] | null): boolean => {
  const left = (a ?? []).map(slugify).sort();
  const right = (b ?? []).map(slugify).sort();
  if (left.length !== right.length) return false;
  return left.every((value, i) => value === right[i]);
};

export const SearchBranch = ({ locale, onSubmit, leftSlot }: SearchBranchProps) => {
  const { t } = useTranslation('common');
  // Optional: outside the MC provider (e.g. the standalone job-list home) there's no currentView
  // to prefill from — the search just starts empty.
  const currentView = useCandidateNavigationOptional()?.currentView;

  const initialFilters = useMemo<CandidateSearchFilters | undefined>(() => {
    if (typeof currentView === 'object' && 'candidateSearch' in currentView) return currentView.candidateSearch;
    if (typeof currentView === 'object' && 'editSearch' in currentView) return currentView.editSearch;
    if (typeof currentView === 'object' && 'keyword' in currentView) return { keyword: currentView.keyword };
    return undefined;
  }, [currentView]);

  const filterDrawer = useContext(FilterDrawerContext);
  const headerSearch = useContext(HeaderSearchContext);
  const appliedFiltersCount = useMemo(
    () => countAppliedFilters(headerSearch?.queryParams),
    [headerSearch?.queryParams],
  );

  // Recover the original phrasing from saved alerts when the URL only carries the
  // slugified keyword (deep links, shared URLs, refreshes). Saved alerts are a
  // logged-in concept — skip the request for guests (/v1/user/next-jobs 401s).
  const { isAuth } = useAuthClient();
  const { data: nextJobs } = useSwrNextJobs({ shouldFetch: isAuth });
  const fallbackAlert = useMemo(() => {
    if (initialFilters?.originalKeyword) return undefined;
    if (!nextJobs || !initialFilters?.keyword) return undefined;
    const targetTitle = slugify(initialFilters.keyword);
    return nextJobs.find((item) => (
      isSearchSourcedRow(item.sourceType)
      && slugify(item.jobTitle) === targetTitle
      && (item.salary ?? undefined) === initialFilters.salary
      && sameLocations(item.locations, initialFilters.locations)
    ));
  }, [nextJobs, initialFilters]);

  const effectiveOriginalKeyword = initialFilters?.originalKeyword ?? fallbackAlert?.originalKeyword ?? undefined;
  const initialKeyword = effectiveOriginalKeyword ?? initialFilters?.keyword ?? '';

  const [keyword, setKeyword] = useState(initialKeyword);
  const [isEditing, setIsEditing] = useState(false);
  const userEditedRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Collapse on blur only when focus leaves the prompt entirely. Clicking the
  // chevron (or its popup icons) moves focus to an element still inside the form,
  // so the expanded view stays open and the extra icons can show.
  const handleBlur = useCallback((e: FocusEvent<HTMLTextAreaElement>) => {
    if (formRef.current?.contains(e.relatedTarget as Node | null)) return;
    setIsEditing(false);
  }, []);

  const { resize } = useAutoResizeTextarea({ ref: textareaRef, value: keyword, maxRows: 3 });

  useEffect(() => {
    if (!isEditing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
    resize();
  }, [isEditing, resize]);

  // Collapse back to the fake-input view when the user clears their text.
  const prevKeywordRef = useRef(keyword);
  useEffect(() => {
    if (isEditing && prevKeywordRef.current.length > 0 && keyword.length === 0) {
      setIsEditing(false);
    }
    prevKeywordRef.current = keyword;
  }, [isEditing, keyword]);

  // Keep the textarea in sync with the view's keyword: the original phrasing when we have it
  // (saved alerts, or the async fallback-alert lookup), otherwise the keyword carried by the URL
  // (e.g. company/location interest edits, which aren't saved alerts). Skip once the user has
  // started typing so we don't clobber their input.
  useEffect(() => {
    if (userEditedRef.current) return;
    if (!initialKeyword) return;
    if (keyword === initialKeyword) return;
    setKeyword(initialKeyword);
  }, [initialKeyword, keyword]);

  const submit = useCallback(() => {
    onSubmit({ keyword: keyword.trim() });
  }, [keyword, onSubmit]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }, [submit]);

  // Voice search (Web Speech API): waveform while listening, prefill on stop — the
  // user reviews and submits manually, nothing auto-submits.
  const handleTranscript = useCallback((text: string) => {
    userEditedRef.current = true;
    setKeyword(text);
    setIsEditing(true);
  }, []);

  const handlePermissionDenied = useCallback(() => {
    bjToast.error(t('audio-interview.microphone-permission.denied.description'));
  }, [t]);

  const {
    isSupported: isVoiceSupported, isRecording, isHintVisible, toggleRecording, getLevel, isWithinSubmitGrace,
  } = useVoiceToText({ locale, onTranscript: handleTranscript, onPermissionDenied: handlePermissionDenied });

  // The engine can end the session under the user's finger, swapping the stop button
  // into a submit button mid-tap — swallow pointer submits right after recording ends.
  // Keyboard-triggered clicks have detail 0 and stay exempt.
  const guardSubmitClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    if (e.detail > 0 && isWithinSubmitGrace()) e.preventDefault();
  }, [isWithinSubmitGrace]);

  const hasText = keyword.trim().length > 0;
  const showSearchButton = (hasText || !isVoiceSupported) && !isRecording;

  const micPopupItem = (
    <PopoverStyledButton
      as="button"
      type="button"
      onClick={toggleRecording}
      aria-label={isRecording ? t('global.label.stop', 'Stop') : t('global.label.voice', 'Voice')}
      title={isRecording ? t('global.label.stop', 'Stop') : t('global.label.voice', 'Voice')}
      className={`flex !h-9 !w-9 items-center justify-center !rounded-lg !p-0 transition-transform hover:scale-110 lg:!h-10 lg:!w-10 ${isRecording ? '!bg-red-500 hover:!bg-red-500' : 'bg-secondary hover:!bg-secondary'}`}
    >
      <MicrophoneIcon className="size-5 text-white" />
    </PopoverStyledButton>
  );

  const searchPopupItem = (
    <PopoverStyledButton
      as="button"
      type="submit"
      form="candidate-prompt-bar-search"
      onClick={guardSubmitClick}
      aria-label={t('mc.search.job')}
      title={t('mc.search.job')}
      className="flex !h-9 !w-9 items-center justify-center !rounded-lg bg-secondary !p-0 transition-transform hover:scale-110 hover:!bg-secondary lg:!h-10 lg:!w-10"
    >
      <MagnifyingGlassIcon className="size-5 text-white" />
    </PopoverStyledButton>
  );

  const moreOptionsPopup = (
    <PopoverStyled className="relative ml-2 shrink-0">
      <PopoverStyledButton
        as="button"
        type="button"
        aria-label={t('global.label.more.options', 'More options')}
        className="relative flex size-7 items-center justify-center rounded-md text-ink-medium transition-colors hover:bg-surface-100 hover:text-ink-medium data-[open]:bg-surface-100"
      >
        <ChevronDownIcon className="size-4" />
        {appliedFiltersCount > 0 && (
          <span
            aria-hidden
            className="pointer-events-none absolute right-0.5 top-0.5 size-1.5 rounded-full bg-ink-medium"
          />
        )}
      </PopoverStyledButton>

      <PopoverStyledPanel
        animation="slideUp"
        position="right"
        className="absolute bottom-full right-0 mb-2 flex items-center gap-2 rounded-xl border border-input bg-surface p-2 shadow-lg"
      >
        <>
          {hasText && !isRecording && isVoiceSupported ? micPopupItem : searchPopupItem}

          {filterDrawer && (
            <div className="relative shrink-0">
              <PopoverStyledButton
                as="button"
                type="button"
                onClick={() => { filterDrawer.toggleDrawer(); }}
                aria-label={t('global.label.filter')}
                title={t('global.label.filter')}
                className="flex !h-9 !w-9 items-center justify-center !rounded-lg !bg-transparent !p-0 !text-ink-medium hover:!bg-surface-100 hover:!text-ink-medium lg:!h-10 lg:!w-10"
              >
                <AdjustmentsHorizontalIcon className="size-5" />
              </PopoverStyledButton>
              {appliedFiltersCount > 0 && (
                <span
                  aria-label={t('global.label.filter')}
                  className="pointer-events-none absolute right-1 top-1 size-2 rounded-full bg-ink-medium"
                />
              )}
            </div>
          )}

          <MapButton
            locale={locale}
            rounding="none"
            className="!h-9 !w-9 shrink-0 !rounded-lg !bg-transparent !p-0 !text-ink-medium hover:!bg-surface-100 hover:!text-ink-medium lg:!h-10 lg:!w-10"
            iconClassName="size-5"
          />

        </>
      </PopoverStyledPanel>
    </PopoverStyled>
  );

  const primaryButton = showSearchButton ? (
    <Button
      type="submit"
      form="candidate-prompt-bar-search"
      styling="outline"
      color="light"
      rounding="none"
      // Prevent textarea blur when clicking — without this, blur collapses the
      // editing UI before the click reaches the submit button.
      onMouseDown={(e) => e.preventDefault()}
      onClick={guardSubmitClick}
      aria-label={t('mc.search.job')}
      title={t('mc.search.job')}
      className="!h-9 !w-9 shrink-0 !rounded-lg bg-secondary !p-0 transition-transform hover:scale-110 hover:!bg-secondary lg:!h-10 lg:!w-10"
    >
      <MagnifyingGlassIcon className="size-5 text-white" />
    </Button>
  ) : (
    <Button
      type="button"
      styling="outline"
      color="light"
      rounding="none"
      onMouseDown={(e) => e.preventDefault()}
      onClick={toggleRecording}
      aria-label={isRecording ? t('global.label.stop', 'Stop') : t('global.label.voice', 'Voice')}
      title={isRecording ? t('global.label.stop', 'Stop') : t('global.label.voice', 'Voice')}
      className={`!h-9 !w-9 shrink-0 !rounded-lg !p-0 transition-transform hover:scale-110 lg:!h-10 lg:!w-10 ${isRecording ? '!bg-red-500 hover:!bg-red-500' : 'bg-secondary hover:!bg-secondary'}`}
    >
      {isRecording ? <StopIcon className="size-5 text-white" /> : <MicrophoneIcon className="size-5 text-white" />}
    </Button>
  );

  const actionButtons = (
    <div className="ml-auto flex items-center gap-1">
      {primaryButton}

      {moreOptionsPopup}
    </div>
  );

  if (!isEditing) {
    return (
      <form
        id="candidate-prompt-bar-search"
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        className="relative flex w-full items-center gap-2"
      >
        {isHintVisible && <VoiceHintBubble />}
        {leftSlot}
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          aria-label={t('extended.keyword.placeholder')}
          className="flex min-w-0 flex-1 px-2 py-1 text-left text-xs leading-normal md:text-sm"
        >
          {isRecording && !keyword ? <VoiceWaveform getLevel={getLevel} /> : (
            keyword
              ? <span className="block w-full truncate text-ink">{keyword}</span>
              : <span className="block w-full truncate text-ink/60">{t('extended.keyword.placeholder')}</span>
          )}
        </button>
        {actionButtons}
      </form>
    );
  }

  return (
    <form
      ref={formRef}
      id="candidate-prompt-bar-search"
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      className="relative flex w-full flex-col gap-1"
    >
      {isHintVisible && <VoiceHintBubble />}
      <div className="relative">
        <textarea
          ref={textareaRef}
          id="candidate_prompt_bar_keyword"
          name="candidate_prompt_bar_keyword"
          rows={1}
          value={keyword}
          onChange={(e) => { userEditedRef.current = true; setKeyword(e.target.value); }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          aria-label={t('extended.keyword.placeholder')}
          className="block w-full resize-none border-none bg-transparent px-2 py-1 text-base leading-normal text-ink shadow-none outline-none focus:ring-0 focus:ring-offset-0 md:text-sm"
        />
        {isRecording && !keyword && (
          <VoiceWaveform getLevel={getLevel} className="pointer-events-none absolute inset-x-2 top-1/2 w-auto -translate-y-1/2" />
        )}
      </div>

      <div className="flex w-full items-center gap-2">
        {leftSlot}
        {actionButtons}
      </div>
    </form>
  );
};
